import React, { useEffect, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  useCreateOfferMutation,
  useUpdateOfferMutation,
} from "store/offer/offerApi";
import { toast } from "react-toastify";
import CustomizedButton from "@components/ButtonConfirm/ButtonConfirm";
import { useForm } from "react-hook-form";

// 📊 Liste fixe des classes comptables
const CLASSES = ["706", "707", "708", "601", "604"];

// 📁 Catégories fixes si API vide
export const FIXED_CATEGORIES = [
  { value: "informatique", label: "offer.create_form.categories.informatique" },
  { value: "nourriture", label: "offer.create_form.categories.nourriture" },
  { value: "electronique", label: "offer.create_form.categories.electronique" },
  { value: "mobilier", label: "offer.create_form.categories.mobilier" },
  { value: "transport", label: "offer.create_form.categories.transport" },
  { value: "services", label: "offer.create_form.categories.services" },
  { value: "marketing", label: "offer.create_form.categories.marketing" },
  { value: "sante", label: "offer.create_form.categories.sante" },
  { value: "construction", label: "offer.create_form.categories.construction" },
  { value: "education", label: "offer.create_form.categories.education" },
];

const CreateOfferComponent = ({ onClose, offer }) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  // States locaux
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isCustomCategory, setIsCustomCategory] = useState(false);

  const [selectedClasse, setSelectedClasse] = useState("");
  const [isCustomClasse, setIsCustomClasse] = useState(false);

  const [offerType, setOfferType] = useState("");

  const [createOffer, { isLoading: isCreating }] = useCreateOfferMutation();
  const [updateOffer, { isLoading: isUpdating }] = useUpdateOfferMutation();

  // ⚙️ Form setup
  const { register, handleSubmit, setValue } = useForm({
    defaultValues: offer
      ? {
          name: offer.nom,
          price: offer.price,
          description: offer.description,
          type: offer.type,
          categorie: offer.categorie,
          classeComptable: offer.classeComptable,
        }
      : {},
  });

  // 🧠 Initialise les valeurs par défaut si on est en mode modification
  useEffect(() => {
    if (offer) {
      setOfferType(offer.type || "");

      // Catégorie
      if (offer.categorie) {
        setSelectedCategory(offer.categorie);
        setIsCustomCategory(
          !FIXED_CATEGORIES.some((c) => c.value === offer.categorie)
        );
      }

      // Classe comptable
      if (offer.classeComptable) {
        setSelectedClasse(offer.classeComptable);
        setIsCustomClasse(!CLASSES.includes(offer.classeComptable));
      }
    }
  }, [offer]);

  // 🟢 Création d'une offre
  const onSubmit = async (data) => {
    try {
      const offerData = {
        nom: data.name,
        type: data.type,
        categorie: isCustomCategory ? data.categorie : selectedCategory,
        price: data.price ? Number(data.price) : undefined,
        description: data.description,
        classeComptable: isCustomClasse ? data.classeComptable : selectedClasse,
        companyId,
      };

      await createOffer(offerData).unwrap();
      toast.success(t("offer.create_form.create_success"));
      onClose();
    } catch {
      toast.error(t("offer.create_form.create_error"));
    }
  };

  // 🔄 Mise à jour d'une offre
  const onUpdate = async (data) => {
    try {
      const offerData = {
        nom: data.name,
        type: data.type,
        price: data.price ? Number(data.price) : undefined,
        description: data.description,
        categorie: isCustomCategory ? data.categorie : selectedCategory,
        classeComptable: isCustomClasse ? data.classeComptable : selectedClasse,
        companyId,
      };

      await updateOffer({ id: offer._id, data: offerData });
      toast.success(t("offer.create_form.update_success"));
      onClose();
    } catch {
      toast.error(t("offer.create_form.update_error"));
    }
  };

  return (
    <>
      {/* Nom & Prix */}
      <div className="row-container">
        <div className="input-container">
          <div className="input-title">{t("offer.create_form.name")}</div>
          <input
            {...register("name", { required: "Le nom est requis" })}
            className="input-content"
            placeholder={t("offer.create_form.name_placeholder")}
          />
        </div>
        <div className="input-container">
          <div className="input-title">{t("offer.create_form.price")}</div>
          <input
            {...register("price")}
            className="input-content"
            type="number"
            placeholder={t("offer.create_form.price_placeholder")}
          />
        </div>
      </div>

      {/* Type */}
      <div className="row-container">
        <div className="input-container">
          <div className="input-title">{t("offer.create_form.type")}</div>
          <select
            {...register("type", { required: "Le type est requis" })}
            value={offerType}
            onChange={(e) => setOfferType(e.target.value)}
            className="input-content">
            <option value="">{t("offer.create_form.type_placeholder")}</option>
            <option value="produit">
              {t("offer.create_form.types.product")}
            </option>
            <option value="service">
              {t("offer.create_form.types.service")}
            </option>
          </select>
        </div>
      </div>

      {/* Description */}
      <div className="row-container">
        <div className="input-container">
          <div className="input-title">
            {t("offer.create_form.description")}
          </div>
          <textarea
            {...register("description", {
              required: "La description est requise",
            })}
            className="input-content"
            placeholder={t("offer.create_form.description_placeholder")}
            rows={2}
          />
        </div>
      </div>

      {/* Catégorie & Classe comptable */}
      <div className="row-container">
        {/* Catégorie */}
        <div className="input-container">
          <div className="input-title">{t("offer.create_form.category")}</div>
          {!isCustomCategory ? (
            <select
              {...register("categorie")}
              className="input-content"
              value={selectedCategory}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "other") {
                  setIsCustomCategory(true);
                  setSelectedCategory("");
                  setValue("categorie", "");
                } else {
                  setSelectedCategory(val);
                  setValue("categorie", val);
                }
              }}>
              <option value="">
                {t("offer.create_form.category_placeholder")}
              </option>
              {FIXED_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {t(cat.label)}
                </option>
              ))}
              <option value="other">
                {t("offer.create_form.category_custom")}
              </option>
            </select>
          ) : (
            <>
              <input
                {...register("categorie")}
                className="input-content mt-2"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setValue("categorie", e.target.value);
                }}
                placeholder={t("offer.create_form.category_placeholder")}
              />
              <button
                type="button"
                className="back-to-list-btn"
                onClick={() => {
                  setIsCustomCategory(false);
                  setSelectedCategory("");
                  setValue("categorie", "");
                }}>
                ← {t("offer.back_to_list")}
              </button>
            </>
          )}
        </div>

        {/* Classe comptable */}
        <div className="input-container">
          <div className="input-title">{t("offer.create_form.class")}</div>
          {!isCustomClasse ? (
            <select
              {...register("classeComptable")}
              className="input-content"
              value={selectedClasse}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "other") {
                  setIsCustomClasse(true);
                  setSelectedClasse("");
                  setValue("classeComptable", "");
                } else {
                  setSelectedClasse(val);
                  setValue("classeComptable", val);
                }
              }}>
              <option value="">
                {t("offer.create_form.class_placeholder")}
              </option>
              {CLASSES.map((cls) => (
                <option key={cls} value={cls}>
                  {cls}
                </option>
              ))}
              <option value="other">
                {t("offer.create_form.class_custom")}
              </option>
            </select>
          ) : (
            <>
              <input
                {...register("classeComptable")}
                className="input-content mt-2"
                value={selectedClasse}
                onChange={(e) => {
                  setSelectedClasse(e.target.value);
                  setValue("classeComptable", e.target.value);
                }}
                placeholder={t("offer.create_form.class_placeholder")}
              />
              <button
                type="button"
                className="back-to-list-btn"
                onClick={() => {
                  setIsCustomClasse(false);
                  setSelectedClasse("");
                  setValue("classeComptable", "");
                }}>
                ← {t("offer.back_to_list")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bouton */}
      <CustomizedButton
        creatingText={t(
          offer ? "offer.create_form.updating" : "offer.create_form.creating"
        )}
        finalizeText={t(
          offer ? "offer.create_form.update" : "offer.create_form.submit"
        )}
        handleCreate={() => {
          if (offer) handleSubmit(onUpdate)();
          else handleSubmit(onSubmit)();
        }}
        isCreating={isCreating || isUpdating}
        className="btn w-100"
      />
    </>
  );
};

export default CreateOfferComponent;
